import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  ArrowLeft,
  Plus,
  Phone,
  Mail,
  Users,
  FileText,
  ShoppingCart,
  MapPin,
  Calendar as CalendarIcon,
  Check,
  Clock,
  Building2,
  Pencil,
  MessageSquare,
  Car,
  Tag,
  Percent,
  Truck,
  CreditCard,
  Briefcase,
  Receipt,
  Loader2,
  Send,
  Euro,
  Globe,
  Eye,
  Package,
} from "lucide-react";
import api from "../lib/api";
import { cn } from "../lib/utils";
import ClientTimeline from "../components/ClientTimeline";

const ACTIVITY_TYPES = [
  { value: "call", label: "Telefonata", icon: Phone, color: "text-blue-500" },
  { value: "email", label: "Email", icon: Mail, color: "text-green-500" },
  { value: "meeting", label: "Riunione", icon: Users, color: "text-purple-500" },
  { value: "visit", label: "Visita", icon: Car, color: "text-orange-500" },
  { value: "note", label: "Nota", icon: MessageSquare, color: "text-gray-500" },
  { value: "quote", label: "Preventivo", icon: FileText, color: "text-amber-500" },
  { value: "order", label: "Ordine", icon: ShoppingCart, color: "text-teal-500" },
  { value: "statement_request", label: "Richiesta Estratto Conto", icon: Receipt, color: "text-indigo-500" },
  { value: "email_sent", label: "Email Inviata", icon: Mail, color: "text-emerald-500" },
];

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [client, setClient] = useState(null);
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [requestingStatement, setRequestingStatement] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // details, activities, documents
  const [clientOrders, setClientOrders] = useState([]);
  const [clientQuotes, setClientQuotes] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [formData, setFormData] = useState({
    activity_type: "call",
    title: "",
    description: "",
    scheduled_at: null,
  });
  
  // Email template states
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailLanguage, setEmailLanguage] = useState("it");
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchClientData();
    fetchTemplates();
  }, [clientId]);

  // Fetch orders and quotes when switching to documents tab
  useEffect(() => {
    if (activeTab === "documents") {
      fetchClientDocuments();
    }
  }, [activeTab, clientId]);

  const fetchTemplates = async () => {
    try {
      const response = await api.get("/email-templates");
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const fetchClientDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const [ordersRes, quotesRes] = await Promise.all([
        api.get(`/orders?client_id=${clientId}&page=1&page_size=100`),
        api.get(`/quotes?client_id=${clientId}&page=1&page_size=100`),
      ]);
      setClientOrders(ordersRes.data.items || []);
      setClientQuotes(quotesRes.data.items || []);
    } catch (error) {
      console.error("Error fetching client documents:", error);
      toast.error("Errore nel caricamento dei documenti");
    } finally {
      setLoadingDocuments(false);
    }
  };

  const fetchClientData = async () => {
    try {
      const [clientRes, activitiesRes, summaryRes, pricingRes] = await Promise.all([
        api.get(`/clients/${clientId}`),
        api.get(`/clients/${clientId}/activities`),
        api.get(`/clients/${clientId}/summary`),
        api.get(`/settings/pricing-info/${clientId}`),
      ]);
      setClient(clientRes.data);
      setActivities(activitiesRes.data);
      setSummary(summaryRes.data);
      setPricingInfo(pricingRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento del cliente");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    try {
      await api.post("/activities", {
        client_id: clientId,
        ...formData,
        scheduled_at: formData.scheduled_at?.toISOString() || null,
      });
      toast.success("Attività creata con successo");
      setDialogOpen(false);
      setFormData({ activity_type: "call", title: "", description: "", scheduled_at: null });
      fetchClientData();
    } catch (error) {
      toast.error("Errore nella creazione dell'attività");
    }
  };

  const handleCompleteActivity = async (activityId) => {
    try {
      await api.put(`/activities/${activityId}/complete`);
      toast.success("Attività completata");
      fetchClientData();
    } catch (error) {
      toast.error("Errore nel completamento dell'attività");
    }
  };

  const handleSendEmail = async () => {
    if (!selectedTemplate) {
      toast.error("Seleziona un template");
      return;
    }

    setSendingEmail(true);
    try {
      await api.post(`/clients/${clientId}/send-email`, {
        template_id: selectedTemplate,
        language: emailLanguage,
      });
      toast.success("Email inviata con successo!");
      setEmailDialogOpen(false);
      setSelectedTemplate(null);
      fetchClientData(); // Refresh activities
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio dell'email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleRequestStatement = async () => {
    if (!window.confirm("Vuoi richiedere l'estratto conto per questo cliente? Verrà inviata un'email all'amministrazione.")) {
      return;
    }
    
    setRequestingStatement(true);
    try {
      await api.post(`/clients/${clientId}/request-statement`);
      toast.success("Richiesta estratto conto inviata con successo! Riceverai una copia via email.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio della richiesta");
    } finally {
      setRequestingStatement(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return format(new Date(dateStr), "dd MMM yyyy, HH:mm", { locale: it });
    } catch {
      return "";
    }
  };

  const getActivityIcon = (type) => {
    const activity = ACTIVITY_TYPES.find((a) => a.value === type);
    if (activity) {
      const Icon = activity.icon;
      return <Icon className={`w-4 h-4 ${activity.color}`} />;
    }
    return <MessageSquare className="w-4 h-4" />;
  };

  const getChannelBadgeClass = (channel) => {
    const classes = {
      Retail: "channel-retail",
      GDO: "channel-gdo",
      DO: "channel-do",
      "Ho.Re.Ca.": "channel-horeca",
      "Distributore": "bg-indigo-100 text-indigo-800 border-indigo-200"
    };
    return `channel-badge ${classes[channel] || ""}`;
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 w-48 bg-muted rounded" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="h-48 bg-muted rounded-xl" />
            <div className="h-96 bg-muted rounded-xl" />
          </div>
          <div className="h-64 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/clients")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-primary font-bold">
                {client?.company_name}
              </h1>
              <span className={getChannelBadgeClass(client?.channel)}>
                {client?.channel}
              </span>
            </div>
            <p className="text-muted-foreground font-mono text-sm">
              {client?.client_code || "N/A"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {(user?.role === "admin" || user?.role === "agent") && (
            <Button 
              variant="outline" 
              onClick={handleRequestStatement}
              disabled={requestingStatement}
              data-testid="request-statement-btn"
            >
              {requestingStatement ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Receipt className="w-4 h-4 mr-2" />
              )}
              Richiedi Estratto Conto
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-accent hover:bg-accent/90" data-testid="add-activity-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Attività
              </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuova Attività CRM</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateActivity} className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo Attività</Label>
                <Select
                  value={formData.activity_type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, activity_type: value })
                  }
                >
                  <SelectTrigger data-testid="activity-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="flex items-center gap-2">
                          <type.icon className={`w-4 h-4 ${type.color}`} />
                          {type.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Titolo *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Es. Chiamata di follow-up"
                  required
                  data-testid="activity-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Dettagli dell'attività..."
                  rows={3}
                  data-testid="activity-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Programmata (opzionale)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.scheduled_at && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.scheduled_at
                        ? format(formData.scheduled_at, "PPP", { locale: it })
                        : "Seleziona data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.scheduled_at}
                      onSelect={(date) => setFormData({ ...formData, scheduled_at: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="activity-submit-btn">
                  Crea Attività
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab("details")}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === "details"
                ? "text-accent border-b-2 border-accent"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Dettagli Cliente
          </button>
          <button
            onClick={() => setActiveTab("activities")}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === "activities"
                ? "text-accent border-b-2 border-accent"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Attività & Timeline
          </button>
          <button
            onClick={() => setActiveTab("documents")}
            className={`pb-4 px-2 font-medium transition-colors flex items-center gap-2 ${
              activeTab === "documents"
                ? "text-accent border-b-2 border-accent"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Package className="w-4 h-4" />
            Ordini & Preventivi
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "activities" ? (
        <ClientTimeline clientId={clientId} />
      ) : activeTab === "documents" ? (
        /* Orders & Quotes Tab */
        <div className="space-y-6">
          {loadingDocuments ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          ) : (
            <>
              {/* Orders Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-accent" />
                    Ordini ({clientOrders.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {clientOrders.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      Nessun ordine trovato per questo cliente
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>N° Ordine</TableHead>
                            <TableHead>Data</TableHead>
                            <TableHead>Stato</TableHead>
                            <TableHead className="text-right">Totale</TableHead>
                            <TableHead className="w-[80px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {clientOrders.map((order) => (
                            <TableRow key={order.id}>
                              <TableCell className="font-mono font-medium">
                                {order.order_number}
                              </TableCell>
                              <TableCell>
                                {order.created_at ? format(new Date(order.created_at), "dd/MM/yyyy", { locale: it }) : "N/A"}
                              </TableCell>
                              <TableCell>
                                <Badge variant={
                                  order.status === "delivered" ? "success" :
                                  order.status === "shipped" ? "default" :
                                  order.status === "confirmed" ? "secondary" :
                                  order.status === "cancelled" ? "destructive" :
                                  "outline"
                                }>
                                  {order.status === "pending" && "In Attesa"}
                                  {order.status === "confirmed" && "Confermato"}
                                  {order.status === "shipped" && "Spedito"}
                                  {order.status === "delivered" && "Consegnato"}
                                  {order.status === "cancelled" && "Annullato"}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-mono font-medium">
                                {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(order.total_amount || 0)}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  asChild
                                >
                                  <Link to={`/orders/${order.id}`}>
                                    <Eye className="w-4 h-4" />
                                  </Link>
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quotes Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-500" />
                    Preventivi ({clientQuotes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {clientQuotes.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      Nessun preventivo trovato per questo cliente
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>N° Preventivo</TableHead>
                            <TableHead>Data</TableHead>
                            <TableHead>Stato</TableHead>
                            <TableHead>Valido Fino</TableHead>
                            <TableHead className="text-right">Totale</TableHead>
                            <TableHead className="w-[80px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {clientQuotes.map((quote) => (
                            <TableRow key={quote.id}>
                              <TableCell className="font-mono font-medium">
                                {quote.quote_number}
                              </TableCell>
                              <TableCell>
                                {quote.created_at ? format(new Date(quote.created_at), "dd/MM/yyyy", { locale: it }) : "N/A"}
                              </TableCell>
                              <TableCell>
                                <Badge variant={
                                  quote.status === "accepted" ? "success" :
                                  quote.status === "converted" ? "default" :
                                  quote.status === "rejected" ? "destructive" :
                                  quote.status === "expired" ? "secondary" :
                                  "outline"
                                }>
                                  {quote.status === "draft" && "Bozza"}
                                  {quote.status === "sent" && "Inviato"}
                                  {quote.status === "accepted" && "Accettato"}
                                  {quote.status === "rejected" && "Rifiutato"}
                                  {quote.status === "expired" && "Scaduto"}
                                  {quote.status === "converted" && "Convertito"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {quote.valid_until ? format(new Date(quote.valid_until), "dd/MM/yyyy", { locale: it }) : "N/A"}
                              </TableCell>
                              <TableCell className="text-right font-mono font-medium">
                                {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(quote.total_amount || 0)}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  asChild
                                >
                                  <Link to={`/quotes/${quote.id}`}>
                                    <Eye className="w-4 h-4" />
                                  </Link>
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
          {/* Discounts Card - Prominent Display */}
          <Card className="border-green-200 dark:border-green-900 bg-green-50/50 dark:bg-green-950/20">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <Tag className="w-5 h-5" />
                Condizioni Commerciali
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {/* Channel Discount */}
                <div className="p-4 rounded-lg bg-white dark:bg-black/20 border border-green-200 dark:border-green-800">
                  <div className="text-xs text-muted-foreground mb-1">Sconto Canale ({client?.channel})</div>
                  <div className="text-2xl font-bold font-mono text-green-600 dark:text-green-400">
                    {pricingInfo?.channel_discount || 0}%
                  </div>
                </div>
                {/* Client Discounts */}
                <div className="p-4 rounded-lg bg-white dark:bg-black/20 border border-green-200 dark:border-green-800">
                  <div className="text-xs text-muted-foreground mb-1">Sconto 1</div>
                  <div className="text-2xl font-bold font-mono text-green-600 dark:text-green-400">
                    {client?.discount_1 || 0}%
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-white dark:bg-black/20 border border-green-200 dark:border-green-800">
                  <div className="text-xs text-muted-foreground mb-1">Sconto 2</div>
                  <div className="text-2xl font-bold font-mono text-green-600 dark:text-green-400">
                    {client?.discount_2 || 0}%
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-white dark:bg-black/20 border border-green-200 dark:border-green-800">
                  <div className="text-xs text-muted-foreground mb-1">Sconto 3</div>
                  <div className="text-2xl font-bold font-mono text-green-600 dark:text-green-400">
                    {client?.discount_3 || 0}%
                  </div>
                </div>
              </div>
              
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm">
                   <CreditCard className="w-4 h-4 text-muted-foreground" />
                   <span className="text-muted-foreground">Pagamento:</span>
                   <span className="font-medium">{client?.payment_method || "Non specificato"}</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-white/50 dark:bg-black/10 rounded-lg text-sm text-muted-foreground">
                <p className="flex items-center gap-2">
                  <Percent className="w-4 h-4" />
                  Gli sconti vengono applicati in cascata sul listino commerciale: prima lo sconto canale, poi Sconto 1, 2 e 3. Infine si aggiungono accisa e IVA 22%.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Client Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-accent" />
                Informazioni Cliente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Anagrafica */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Dati Fiscali & Sede
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <Label className="text-muted-foreground text-xs">P.IVA / C.F.</Label>
                      <p className="font-mono">{client?.vat_number || "-"} / {client?.fiscal_code || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Codice SDI / PEC</Label>
                      <p className="font-mono">{client?.sdi_code || "-"} / {client?.pec || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Indirizzo Sede</Label>
                      <p className="font-medium flex items-start gap-1">
                        <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                        <span>
                          {client?.address}<br/>
                          {client?.postal_code} {client?.city} ({client?.region})
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contatti */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Contatti Aziendali
                  </h3>
                  <div className="space-y-3 text-sm">
                    {/* Contatto Principale */}
                    <div className="p-3 rounded-lg bg-muted/30 border">
                      <Label className="text-muted-foreground text-xs">Contatto Principale</Label>
                      <p className="font-medium mt-1">{client?.contact_name || "-"}</p>
                      <div className="flex flex-col gap-1 mt-2 text-muted-foreground text-xs">
                        {client?.contact_email && (
                          <span className="flex items-center gap-2">
                            <Mail className="w-3 h-3"/> {client.contact_email}
                          </span>
                        )}
                        <span className="flex items-center gap-2">
                          <Mail className="w-3 h-3"/> {client?.email} <span className="text-accent">(login)</span>
                        </span>
                        {client?.phone && (
                          <span className="flex items-center gap-2">
                            <Phone className="w-3 h-3"/> {client.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Riferimenti Amministrativi */}
                    {(client?.admin_contact_name || client?.admin_contact_email || client?.admin_contact_phone) && (
                      <div className="p-3 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                        <Label className="text-muted-foreground text-xs">Riferimenti Amministrativi</Label>
                        <p className="font-medium mt-1">{client?.admin_contact_name || "-"}</p>
                        <div className="flex flex-col gap-1 mt-2 text-muted-foreground text-xs">
                          {client?.admin_contact_email && (
                            <span className="flex items-center gap-2">
                              <Mail className="w-3 h-3"/> {client.admin_contact_email}
                            </span>
                          )}
                          {client?.admin_contact_phone && (
                            <span className="flex items-center gap-2">
                              <Phone className="w-3 h-3"/> {client.admin_contact_phone}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Indirizzo Consegna - Full Width */}
              {(client?.delivery_address || client?.delivery_contact) && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4" />
                    Indirizzo di Consegna
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-muted-foreground text-xs">Indirizzo</Label>
                      <p className="font-medium">
                        {client?.delivery_address || client?.address}<br/>
                        {client?.delivery_postal_code || client?.postal_code} {client?.delivery_city || client?.city}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Referente Magazzino</Label>
                      <p className="font-medium">{client?.delivery_contact || client?.contact_name}</p>
                      {client?.delivery_notes && (
                        <p className="text-muted-foreground text-xs mt-1">Note: {client.delivery_notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {client?.notes && (
                <div className="mt-6 pt-6 border-t">
                  <Label className="text-muted-foreground text-xs">Note Generali</Label>
                  <p className="text-sm mt-1">{client.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-accent" />
                Storico Attività
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna attività registrata</p>
                  <Button
                    variant="link"
                    onClick={() => setDialogOpen(true)}
                    className="mt-2 text-accent"
                  >
                    Crea la prima attività
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {activities.map((activity, index) => (
                    <div
                      key={activity.id}
                      className={`relative pl-8 pb-4 ${
                        index < activities.length - 1 ? "border-l-2 border-border ml-2" : "ml-2"
                      }`}
                      data-testid={`activity-${activity.id}`}
                    >
                      <div className="absolute -left-2 top-0 w-6 h-6 rounded-full bg-card border-2 border-border flex items-center justify-center">
                        {getActivityIcon(activity.activity_type)}
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className="font-medium">{activity.title}</h4>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(activity.created_at)}
                            </p>
                          </div>
                          {!activity.completed && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCompleteActivity(activity.id)}
                              data-testid={`complete-activity-${activity.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Completa
                            </Button>
                          )}
                          {activity.completed && (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              Completata
                            </span>
                          )}
                        </div>
                        {activity.description && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {activity.description}
                          </p>
                        )}
                        {activity.scheduled_at && (
                          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            Programmata: {formatDate(activity.scheduled_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Riepilogo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Fatturato Totale</span>
                <span className="font-mono font-bold text-accent">
                  {formatCurrency(summary?.total_revenue || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Ordini</span>
                <span className="font-mono font-bold">{summary?.orders_count || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Preventivi</span>
                <span className="font-mono font-bold">{summary?.quotes_count || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Attività</span>
                <span className="font-mono font-bold">{summary?.activities_count || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Azioni Rapide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/orders?client=${clientId}`)}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Nuovo Ordine
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/quotes?client=${clientId}`)}
              >
                <FileText className="w-4 h-4 mr-2" />
                Nuovo Preventivo
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => setDialogOpen(true)}
              >
                <Phone className="w-4 h-4 mr-2" />
                Registra Chiamata
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                onClick={() => setEmailDialogOpen(true)}
              >
                <Mail className="w-4 h-4 mr-2" />
                Invia Email con Template
              </Button>
            </CardContent>
          </Card>

          {/* Send Email Dialog */}
          <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Invia Email al Cliente</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm"><strong>Destinatario:</strong> {client?.email}</p>
                  <p className="text-sm"><strong>Azienda:</strong> {client?.company_name}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Lingua</Label>
                  <Select value={emailLanguage} onValueChange={setEmailLanguage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="it">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          Italiano
                        </div>
                      </SelectItem>
                      <SelectItem value="en">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          English
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Template Email</Label>
                  {templates.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-3 bg-muted rounded">
                      Nessun template disponibile. Vai su Template Email per crearne uno.
                    </p>
                  ) : (
                    <Select value={selectedTemplate || ""} onValueChange={setSelectedTemplate}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona un template..." />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            <div className="flex items-center gap-2">
                              {template.type === "presentation" && <Send className="w-4 h-4 text-blue-500" />}
                              {template.type === "catalog" && <FileText className="w-4 h-4 text-purple-500" />}
                              {template.type === "pricelist" && <Euro className="w-4 h-4 text-green-500" />}
                              {template.type === "other" && <Mail className="w-4 h-4 text-gray-500" />}
                              {template.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {selectedTemplate && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                    <p className="font-medium text-blue-700 dark:text-blue-300">
                      Anteprima template
                    </p>
                    {(() => {
                      const tpl = templates.find(t => t.id === selectedTemplate);
                      if (!tpl) return null;
                      return (
                        <div className="mt-2 text-muted-foreground">
                          <p><strong>Oggetto:</strong> {emailLanguage === "en" ? tpl.subject_en : tpl.subject_it}</p>
                          {tpl.attachments && tpl.attachments.length > 0 && (
                            <p className="mt-1"><strong>Allegati:</strong> {tpl.attachments.length} file</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                <Button 
                  onClick={handleSendEmail} 
                  className="w-full" 
                  disabled={!selectedTemplate || sendingEmail}
                >
                  {sendingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Invia Email
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Recent Orders */}
          {summary?.recent_orders?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Ultimi Ordini</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {summary.recent_orders.slice(0, 3).map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between text-sm p-2 bg-muted/30 rounded"
                  >
                    <span className="font-mono text-xs">{order.order_number}</span>
                    <span className="font-medium">{formatCurrency(order.total_amount)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
